import React, { useState } from 'react';
import {
    Box,
    Container,
    Heading,
    Text,
    Flex,
    Button,
    FormControl,
    FormLabel,
    Input,
    Alert,
    AlertIcon,
    AlertTitle,
    Switch,
    useBreakpointValue,
    Grid,
    GridItem,
    SimpleGrid,
    Stack,
    HStack,
    VStack,
    Spacer,
    Center,
    Square,
    Circle,
    Image,
    Card,
    CardHeader,
    CardBody,
    CardFooter,
    Badge,
    Avatar,
    AvatarGroup,
    Divider,
    IconButton,
    Menu,
    MenuButton,
    MenuList,
    MenuItem,
    useColorModeValue,
    useColorMode,
} from '@chakra-ui/react';
import { FiMenu, FiHeart, FiStar, FiShoppingCart, FiUser, FiSettings } from 'react-icons/fi';

const SandboxExample: React.FC = () => {
    const [currentSection, setCurrentSection] = useState('responsive');
    const { colorMode, toggleColorMode } = useColorMode();
    
    // Responsive values
    const sidebarWidth = useBreakpointValue({ base: 'full', md: '250px' });
    const direction = useBreakpointValue({ base: 'column', md: 'row' });
    const gridColumns = useBreakpointValue({ base: 1, md: 2, lg: 3 });
    const spacing = useBreakpointValue({ base: 4, md: 6, lg: 8 });
    const fontSize = useBreakpointValue({ base: 'md', md: 'lg', lg: 'xl' });
    
    // Color mode values
    const bgColor = useColorModeValue('gray.50', 'gray.900');
    const cardBg = useColorModeValue('white', 'gray.800');
    const borderColor = useColorModeValue('gray.200', 'gray.600');

    // Sample data
    const products = [
        { id: 1, name: 'Product 1', price: '$99', image: 'https://via.placeholder.com/200', rating: 4.5 },
        { id: 2, name: 'Product 2', price: '$149', image: 'https://via.placeholder.com/200', rating: 4.8 },
        { id: 3, name: 'Product 3', price: '$79', image: 'https://via.placeholder.com/200', rating: 4.2 },
        { id: 4, name: 'Product 4', price: '$199', image: 'https://via.placeholder.com/200', rating: 4.9 },
        { id: 5, name: 'Product 5', price: '$129', image: 'https://via.placeholder.com/200', rating: 4.6 },
        { id: 6, name: 'Product 6', price: '$89', image: 'https://via.placeholder.com/200', rating: 4.3 },
    ];

    const menuItems = [
        { id: 'responsive', label: 'Responsive Design', icon: FiSettings },
        { id: 'flexbox', label: 'Flexbox Layout', icon: FiMenu },
        { id: 'grid', label: 'Grid System', icon: FiUser },
        { id: 'cards', label: 'Card Components', icon: FiShoppingCart },
        { id: 'navigation', label: 'Navigation', icon: FiStar },
    ];

    const renderSection = () => {
        switch (currentSection) {
            case 'responsive':
                return <ResponsiveSection products={products} />;
            case 'flexbox':
                return <FlexboxSection />;
            case 'grid':
                return <GridSection products={products} />;
            case 'cards':
                return <CardsSection products={products} />;
            case 'navigation':
                return <NavigationSection />;
            default:
                return <ResponsiveSection products={products} />;
        }
    };

    return (
        <Box minH="100vh" bg={bgColor}>
            {/* Header */}
            <Box 
                bg={cardBg} 
                borderBottom="1px" 
                borderColor={borderColor}
                position="sticky"
                top="0"
                zIndex="1000"
                shadow="sm"
            >
                <Container maxW="7xl">
                    <Flex h="16" alignItems="center" justifyContent="space-between">
                        <Heading size="lg" color="blue.500">
                            Chakra UI Sandbox
                        </Heading>
                        <HStack spacing={4}>
                            <Text fontSize="sm">Mode: {colorMode}</Text>
                            <Switch 
                                colorScheme="blue" 
                                onChange={toggleColorMode}
                                isChecked={colorMode === 'dark'}
                            />
                        </HStack>
                    </Flex>
                </Container>
            </Box>

            <Container maxW="7xl" py={8}>
                <Flex direction={direction} gap={spacing}>
                    {/* Sidebar */}
                    <Box 
                        w={sidebarWidth}
                        bg={cardBg}
                        p={6}
                        borderRadius="lg"
                        border="1px"
                        borderColor={borderColor}
                        h="fit-content"
                        position="sticky"
                        top="24"
                    >
                        <Heading size="md" mb={4}>Sections</Heading>
                        <VStack spacing={2} align="stretch">
                            {menuItems.map((item) => (
                                <Button
                                    key={item.id}
                                    variant={currentSection === item.id ? 'solid' : 'ghost'}
                                    colorScheme={currentSection === item.id ? 'blue' : 'gray'}
                                    justifyContent="flex-start"
                                    leftIcon={<item.icon />}
                                    onClick={() => setCurrentSection(item.id)}
                                >
                                    {item.label}
                                </Button>
                            ))}
                        </VStack>
                    </Box>

                    {/* Main Content */}
                    <Box flex="1">
                        {renderSection()}
                    </Box>
                </Flex>
            </Container>
        </Box>
    );
};

// Responsive Design Section
const ResponsiveSection: React.FC<{ products: any[] }> = ({ products }) => {
    const bgColor = useColorModeValue('white', 'gray.800');
    const borderColor = useColorModeValue('gray.200', 'gray.600');
    
    return (
        <VStack spacing={8} align="stretch">
            <Box bg={bgColor} p={6} borderRadius="lg" border="1px" borderColor={borderColor}>
                <Heading size="lg" mb={4} color="blue.500">Responsive Design Basics</Heading>
                <Text mb={4}>
                    Chakra UI menggunakan breakpoint system untuk membuat UI yang responsive. 
                    Breakpoints default: base (0px), sm (480px), md (768px), lg (992px), xl (1280px), 2xl (1536px)
                </Text>
                
                {/* Responsive Box Example */}
                <Box 
                    bg="blue.500" 
                    color="white" 
                    p={{ base: 4, md: 6, lg: 8 }}
                    borderRadius="md"
                    textAlign="center"
                    fontSize={{ base: 'sm', md: 'md', lg: 'lg' }}
                    mb={4}
                >
                    Box ini berubah padding dan font size berdasarkan screen size
                </Box>

                {/* Responsive Grid */}
                <SimpleGrid 
                    columns={{ base: 1, sm: 2, md: 3, lg: 4 }} 
                    spacing={4}
                    mb={4}
                >
                    {[1, 2, 3, 4].map((item) => (
                        <Box 
                            key={item}
                            bg="green.500" 
                            color="white" 
                            p={4} 
                            borderRadius="md"
                            textAlign="center"
                        >
                            Item {item}
                        </Box>
                    ))}
                </SimpleGrid>

                {/* useBreakpointValue Example */}
                <Alert status="info">
                    <AlertIcon />
                    <AlertTitle>useBreakpointValue Hook:</AlertTitle>
                    <Text ml={2}>
                        Columns saat ini: {useBreakpointValue({ base: 1, sm: 2, md: 3, lg: 4 })}
                    </Text>
                </Alert>
            </Box>
        </VStack>
    );
};

// Flexbox Layout Section
const FlexboxSection: React.FC = () => {
    const bgColor = useColorModeValue('white', 'gray.800');
    const borderColor = useColorModeValue('gray.200', 'gray.600');
    
    return (
        <VStack spacing={8} align="stretch">
            <Box bg={bgColor} p={6} borderRadius="lg" border="1px" borderColor={borderColor}>
                <Heading size="lg" mb={4} color="purple.500">Flexbox Layouts</Heading>
                
                {/* Basic Flex */}
                <Box mb={6}>
                    <Heading size="md" mb={3}>Basic Flex Layout</Heading>
                    <Flex 
                        bg="purple.100" 
                        p={4} 
                        borderRadius="md" 
                        gap={4}
                        direction={{ base: 'column', md: 'row' }}
                    >
                        <Box bg="purple.500" color="white" p={4} borderRadius="md" flex="1">
                            Flex Item 1
                        </Box>
                        <Box bg="purple.600" color="white" p={4} borderRadius="md" flex="2">
                            Flex Item 2 (flex: 2)
                        </Box>
                        <Box bg="purple.700" color="white" p={4} borderRadius="md" flex="1">
                            Flex Item 3
                        </Box>
                    </Flex>
                </Box>

                {/* Flex with Spacer */}
                <Box mb={6}>
                    <Heading size="md" mb={3}>Flex with Spacer</Heading>
                    <Flex bg="blue.100" p={4} borderRadius="md" align="center">
                        <Box bg="blue.500" color="white" p={3} borderRadius="md">
                            Left Item
                        </Box>
                        <Spacer />
                        <Box bg="blue.600" color="white" p={3} borderRadius="md">
                            Right Item
                        </Box>
                    </Flex>
                </Box>

                {/* HStack and VStack */}
                <Box mb={6}>
                    <Heading size="md" mb={3}>HStack and VStack</Heading>
                    <Flex direction={{ base: 'column', md: 'row' }} gap={4}>
                        <Box flex="1">
                            <Text mb={2} fontWeight="bold">HStack:</Text>
                            <HStack spacing={4} bg="green.100" p={4} borderRadius="md">
                                <Circle size="40px" bg="green.500" color="white">1</Circle>
                                <Circle size="40px" bg="green.600" color="white">2</Circle>
                                <Circle size="40px" bg="green.700" color="white">3</Circle>
                            </HStack>
                        </Box>
                        <Box flex="1">
                            <Text mb={2} fontWeight="bold">VStack:</Text>
                            <VStack spacing={4} bg="orange.100" p={4} borderRadius="md">
                                <Square size="40px" bg="orange.500" color="white">A</Square>
                                <Square size="40px" bg="orange.600" color="white">B</Square>
                                <Square size="40px" bg="orange.700" color="white">C</Square>
                            </VStack>
                        </Box>
                    </Flex>
                </Box>

                {/* Center Component */}
                <Box>
                    <Heading size="md" mb={3}>Center Component</Heading>
                    <Center bg="teal.100" h="100px" borderRadius="md">
                        <Box bg="teal.500" color="white" p={4} borderRadius="md">
                            Perfectly Centered
                        </Box>
                    </Center>
                </Box>
            </Box>
        </VStack>
    );
};

// Grid System Section
const GridSection: React.FC<{ products: any[] }> = ({ products }) => {
    const bgColor = useColorModeValue('white', 'gray.800');
    const borderColor = useColorModeValue('gray.200', 'gray.600');
    
    return (
        <VStack spacing={8} align="stretch">
            <Box bg={bgColor} p={6} borderRadius="lg" border="1px" borderColor={borderColor}>
                <Heading size="lg" mb={4} color="green.500">Grid System</Heading>
                
                {/* Basic Grid */}
                <Box mb={6}>
                    <Heading size="md" mb={3}>Basic Grid Layout</Heading>
                    <Grid templateColumns="repeat(3, 1fr)" gap={4} mb={4}>
                        {[1, 2, 3, 4, 5, 6].map((item) => (
                            <GridItem key={item}>
                                <Box bg="green.500" color="white" p={4} borderRadius="md" textAlign="center">
                                    Grid {item}
                                </Box>
                            </GridItem>
                        ))}
                    </Grid>
                </Box>

                {/* Advanced Grid */}
                <Box mb={6}>
                    <Heading size="md" mb={3}>Advanced Grid Layout</Heading>
                    <Grid
                        templateRows="repeat(3, 1fr)"
                        templateColumns="repeat(4, 1fr)"
                        gap={4}
                        h="200px"
                    >
                        <GridItem rowSpan={2} colSpan={2} bg="blue.500" borderRadius="md" />
                        <GridItem colSpan={2} bg="green.500" borderRadius="md" />
                        <GridItem colSpan={2} bg="purple.500" borderRadius="md" />
                        <GridItem rowSpan={1} colSpan={4} bg="orange.500" borderRadius="md" />
                    </Grid>
                </Box>

                {/* Responsive Grid with Products */}
                <Box>
                    <Heading size="md" mb={3}>Responsive Product Grid</Heading>
                    <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={6}>
                        {products.slice(0, 6).map((product) => (
                            <Card key={product.id} bg={bgColor} border="1px" borderColor={borderColor}>
                                <CardBody>
                                    <Image
                                        src={product.image}
                                        alt={product.name}
                                        borderRadius="md"
                                        mb={4}
                                    />
                                    <Heading size="sm" mb={2}>{product.name}</Heading>
                                    <Text color="blue.600" fontSize="lg" fontWeight="bold">
                                        {product.price}
                                    </Text>
                                    <HStack mt={2}>
                                        <HStack>
                                            {[...Array(5)].map((_, i) => (
                                                <FiStar 
                                                    key={i} 
                                                    color={i < Math.floor(product.rating) ? '#F6AD55' : '#E2E8F0'} 
                                                />
                                            ))}
                                        </HStack>
                                        <Text fontSize="sm" color="gray.600">
                                            ({product.rating})
                                        </Text>
                                    </HStack>
                                </CardBody>
                            </Card>
                        ))}
                    </SimpleGrid>
                </Box>
            </Box>
        </VStack>
    );
};

// Cards Section
const CardsSection: React.FC<{ products: any[] }> = ({ products }) => {
    const bgColor = useColorModeValue('white', 'gray.800');
    const borderColor = useColorModeValue('gray.200', 'gray.600');
    
    return (
        <VStack spacing={8} align="stretch">
            <Box bg={bgColor} p={6} borderRadius="lg" border="1px" borderColor={borderColor}>
                <Heading size="lg" mb={4} color="pink.500">Card Components</Heading>
                
                {/* Basic Card */}
                <Box mb={6}>
                    <Heading size="md" mb={3}>Basic Cards</Heading>
                    <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
                        <Card bg={bgColor} border="1px" borderColor={borderColor}>
                            <CardHeader>
                                <Flex alignItems="center">
                                    <Avatar name="John Doe" size="sm" mr={3} />
                                    <Box>
                                        <Heading size="sm">John Doe</Heading>
                                        <Text fontSize="sm" color="gray.500">2 hours ago</Text>
                                    </Box>
                                </Flex>
                            </CardHeader>
                            <CardBody>
                                <Text>
                                    This is a sample card with header, body, and footer sections.
                                    Cards are perfect for displaying structured content.
                                </Text>
                            </CardBody>
                            <CardFooter>
                                <HStack spacing={4}>
                                    <Button leftIcon={<FiHeart />} variant="ghost" size="sm">
                                        Like
                                    </Button>
                                    <Button leftIcon={<FiShoppingCart />} variant="ghost" size="sm">
                                        Share
                                    </Button>
                                </HStack>
                            </CardFooter>
                        </Card>

                        <Card bg={bgColor} border="1px" borderColor={borderColor}>
                            <CardBody>
                                <Stack spacing={3}>
                                    <Badge colorScheme="green" w="fit-content">New</Badge>
                                    <Heading size="md">Feature Update</Heading>
                                    <Text>
                                        We've added new responsive design features to make your 
                                        layouts even better across all devices.
                                    </Text>
                                    <HStack>
                                        <Avatar size="xs" name="Team" />
                                        <Text fontSize="sm" color="gray.500">Development Team</Text>
                                    </HStack>
                                </Stack>
                            </CardBody>
                        </Card>
                    </SimpleGrid>
                </Box>

                {/* Product Cards */}
                <Box>
                    <Heading size="md" mb={3}>Product Cards</Heading>
                    <SimpleGrid columns={{ base: 1, sm: 2, lg: 3 }} spacing={6}>
                        {products.slice(0, 3).map((product) => (
                            <Card key={product.id} bg={bgColor} border="1px" borderColor={borderColor} overflow="hidden">
                                <Box position="relative">
                                    <Image
                                        src={product.image}
                                        alt={product.name}
                                        h="200px"
                                        w="100%"
                                        objectFit="cover"
                                    />
                                    <IconButton
                                        aria-label="Add to favorites"
                                        icon={<FiHeart />}
                                        position="absolute"
                                        top={2}
                                        right={2}
                                        colorScheme="whiteAlpha"
                                        variant="solid"
                                        size="sm"
                                    />
                                </Box>
                                <CardBody>
                                    <Stack spacing={3}>
                                        <Heading size="sm">{product.name}</Heading>
                                        <HStack justify="space-between">
                                            <Text color="blue.600" fontSize="xl" fontWeight="bold">
                                                {product.price}
                                            </Text>
                                            <HStack>
                                                <FiStar color="#F6AD55" />
                                                <Text fontSize="sm">{product.rating}</Text>
                                            </HStack>
                                        </HStack>
                                    </Stack>
                                </CardBody>
                                <CardFooter pt={0}>
                                    <Button colorScheme="blue" size="sm" width="100%">
                                        Add to Cart
                                    </Button>
                                </CardFooter>
                            </Card>
                        ))}
                    </SimpleGrid>
                </Box>
            </Box>
        </VStack>
    );
};

// Navigation Section
const NavigationSection: React.FC = () => {
    const bgColor = useColorModeValue('white', 'gray.800');
    const borderColor = useColorModeValue('gray.200', 'gray.600');
    
    return (
        <VStack spacing={8} align="stretch">
            <Box bg={bgColor} p={6} borderRadius="lg" border="1px" borderColor={borderColor}>
                <Heading size="lg" mb={4} color="orange.500">Navigation Components</Heading>
                
                {/* Top Navigation Bar */}
                <Box mb={6}>
                    <Heading size="md" mb={3}>Top Navigation Bar</Heading>
                    <Box bg="blue.500" color="white" p={4} borderRadius="md">
                        <Flex alignItems="center" justify="space-between">
                            <HStack spacing={8}>
                                <Heading size="md">Brand</Heading>
                                <HStack spacing={6} display={{ base: 'none', md: 'flex' }}>
                                    <Text cursor="pointer" _hover={{ textDecoration: 'underline' }}>Home</Text>
                                    <Text cursor="pointer" _hover={{ textDecoration: 'underline' }}>Products</Text>
                                    <Text cursor="pointer" _hover={{ textDecoration: 'underline' }}>About</Text>
                                    <Text cursor="pointer" _hover={{ textDecoration: 'underline' }}>Contact</Text>
                                </HStack>
                            </HStack>
                            <HStack>
                                <IconButton
                                    aria-label="Menu"
                                    icon={<FiMenu />}
                                    variant="ghost"
                                    color="white"
                                    display={{ base: 'flex', md: 'none' }}
                                />
                                <Avatar size="sm" name="User" />
                            </HStack>
                        </Flex>
                    </Box>
                </Box>

                {/* Breadcrumb */}
                <Box mb={6}>
                    <Heading size="md" mb={3}>Breadcrumb Navigation</Heading>
                    <HStack spacing={2} p={4} bg="gray.100" borderRadius="md">
                        <Text color="blue.500" cursor="pointer">Home</Text>
                        <Text>/</Text>
                        <Text color="blue.500" cursor="pointer">Products</Text>
                        <Text>/</Text>
                        <Text color="gray.600">Current Page</Text>
                    </HStack>
                </Box>

                {/* Tab Navigation */}
                <Box mb={6}>
                    <Heading size="md" mb={3}>Tab Navigation</Heading>
                    <Box border="1px" borderColor={borderColor} borderRadius="md">
                        <Flex bg="gray.50" borderTopRadius="md">
                            {['Tab 1', 'Tab 2', 'Tab 3'].map((tab, index) => (
                                <Box
                                    key={tab}
                                    flex="1"
                                    p={4}
                                    textAlign="center"
                                    cursor="pointer"
                                    bg={index === 0 ? 'white' : 'transparent'}
                                    borderBottom={index === 0 ? '2px' : '1px'}
                                    borderBottomColor={index === 0 ? 'blue.500' : 'gray.200'}
                                    color={index === 0 ? 'blue.500' : 'gray.600'}
                                    fontWeight={index === 0 ? 'semibold' : 'normal'}
                                >
                                    {tab}
                                </Box>
                            ))}
                        </Flex>
                        <Box p={6} bg="white" borderBottomRadius="md">
                            <Text>Content for the active tab goes here.</Text>
                        </Box>
                    </Box>
                </Box>

                {/* Footer Navigation */}
                <Box>
                    <Heading size="md" mb={3}>Footer Navigation</Heading>
                    <Box bg="gray.800" color="white" p={6} borderRadius="md">
                        <SimpleGrid columns={{ base: 1, md: 4 }} spacing={8}>
                            <VStack align="start" spacing={3}>
                                <Heading size="sm">Company</Heading>
                                <Text fontSize="sm" cursor="pointer" _hover={{ color: 'blue.300' }}>About Us</Text>
                                <Text fontSize="sm" cursor="pointer" _hover={{ color: 'blue.300' }}>Careers</Text>
                                <Text fontSize="sm" cursor="pointer" _hover={{ color: 'blue.300' }}>News</Text>
                            </VStack>
                            <VStack align="start" spacing={3}>
                                <Heading size="sm">Products</Heading>
                                <Text fontSize="sm" cursor="pointer" _hover={{ color: 'blue.300' }}>Features</Text>
                                <Text fontSize="sm" cursor="pointer" _hover={{ color: 'blue.300' }}>Pricing</Text>
                                <Text fontSize="sm" cursor="pointer" _hover={{ color: 'blue.300' }}>Demo</Text>
                            </VStack>
                            <VStack align="start" spacing={3}>
                                <Heading size="sm">Support</Heading>
                                <Text fontSize="sm" cursor="pointer" _hover={{ color: 'blue.300' }}>Help Center</Text>
                                <Text fontSize="sm" cursor="pointer" _hover={{ color: 'blue.300' }}>Contact</Text>
                                <Text fontSize="sm" cursor="pointer" _hover={{ color: 'blue.300' }}>Status</Text>
                            </VStack>
                            <VStack align="start" spacing={3}>
                                <Heading size="sm">Social</Heading>
                                <HStack>
                                    <Circle size="8" bg="blue.500" cursor="pointer">T</Circle>
                                    <Circle size="8" bg="blue.600" cursor="pointer">F</Circle>
                                    <Circle size="8" bg="pink.500" cursor="pointer">I</Circle>
                                </HStack>
                            </VStack>
                        </SimpleGrid>
                        <Divider my={6} />
                        <Text textAlign="center" fontSize="sm" color="gray.400">
                            © 2025 Your Company. All rights reserved.
                        </Text>
                    </Box>
                </Box>
            </Box>
        </VStack>
    );
};

export default SandboxExample;